import { LoadingButton } from "@mui/lab";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { CrossIcon } from "../../assets/icons";
import { DriverListResponse } from "../../openapi/client";
import { IDialogProps } from "../../openapi/interfaces";
import { setNotification } from "../../store/notificationSlice";
import Transition from "../Transition";
import useValidation from "../Validation";
import Api from "../../openapi";
import { Toggle } from "../Switch";
import { checkEmpty } from "../../utils";

interface IDriverCrud extends IDialogProps {
  driver?: DriverListResponse;
}

export default function DriverCrud(props: IDriverCrud) {
  const dummyUser = {
    fullName: "",
    deliveryPhoneNumber: "",
    active: true,
    order: "",
  };
  const dispatch = useDispatch();
  const [driver, setDriverEdit] = useState<any>(dummyUser);
  const { validation, setValidation } = useValidation(driver);
  const [loading, setLoading] = useState(false);
  const [formDirty, setFormDirty] = useState(false);

  const handleFieldChange = (field: string, value: any) => {
    setFormDirty(true);
    if (field === "deliveryPhoneNumber") {
      setDriverEdit((state: any) => ({
        ...state,
        deliveryPhoneNumber: checkEmpty(value) ? "" : `+974${value}`,
      }));
    } else {
      setDriverEdit((state: any) => ({ ...state, [field]: value }));
    }
  };

  const resetForm = () => {
    setDriverEdit(dummyUser);
    setValidation((state) => ({ ...state, show: false }));
  };

  const handlePanelClose = (...args: any) => {
    resetForm();
    props.onClose(args);
  };

  const saveChanges = async () => {
    if (validation.error) {
      setValidation((state) => ({ ...state, show: true }));
      return;
    }

    const createUserDetail: any = {
      id: driver.id,
      fullName: driver.fullName,
      phoneNumber: driver.deliveryPhoneNumber,
      active: driver.active,
      order: driver.order,
    };

    try {
      setLoading(true);
      let message;

      if (props.create) {
        message = "Driver has been created successfully";
        await Api.Driver.createDriver(createUserDetail);
        resetForm();
        props.onConfirm(true);
        props.onClose({});
      } else if (props.edit) {
        message = "Driver has been updated successfully";
        const { id, ...UpdateDriverReqDto } = createUserDetail;
        await Api.Driver.updateDriver(String(id), UpdateDriverReqDto);
      }
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message,
        })
      );
      props.onConfirm(true);
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    setLoading(false);
    setFormDirty(false);
  };
  // console.log("validation", validation);
  // console.log("driver", driver);

  useEffect(() => {
    const getDriverById = async (driverId: string) => {
      try {
        const res = await Api.Driver.getById(driverId);
        const driver = res.data;
        setDriverEdit({
          id: driver.id,
          fullName: driver.fullName,
          deliveryPhoneNumber: driver.phoneNumber,
          active: driver.active,
          order: driver.order,
        });
      } catch (error) {}
    };
    if (props.driver) {
      getDriverById(String(props.driver.id));
    }
  }, [props.driver]);

  return (
    <Fragment>
      <Dialog
        open={props.open}
        TransitionComponent={Transition}
        onClose={handlePanelClose}
        aria-describedby="alert-dialog-slide-description"
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "16px",
            boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
            padding: "24px",
            width: "564px",
            maxWidth: "564px",
          },
          "& .MuiDialogTitle-root": {
            padding: "0 16px 16px 0",
          },
          "& .MuiDialogContent-root,& .MuiDialogActions-root": {
            padding: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "text.dark",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "20px",
          }}
        >
          {props.title}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handlePanelClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.fontLight2",
          }}
        >
          <CrossIcon />
        </IconButton>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
            <Box sx={{ display: "flex", columnGap: "12px", alignItems: "baseline" }}>
              <Box className="form-section">
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" className="form-label">
                    Name / Surname
                  </Typography>
                  <Typography component="span" className="required-label">
                    *
                  </Typography>
                </Box>
                <TextField
                  className="form-field"
                  value={driver.fullName}
                  error={validation.show && validation.fullName?.error}
                  helperText={
                    validation.show && validation.fullName?.error ? validation.fullName.helperText : undefined
                  }
                  onChange={(e) => handleFieldChange("fullName", e.target.value)}
                />
              </Box>
              <Box className="form-section">
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" className="form-label">
                    Telephone
                  </Typography>
                </Box>
                <TextField
                  className="form-field"
                  value={driver?.deliveryPhoneNumber?.slice(4)}
                  onChange={(e) => handleFieldChange("deliveryPhoneNumber", e.target.value)}
                  error={validation.show && validation.deliveryPhoneNumber?.error}
                  helperText={
                    validation.show && validation.deliveryPhoneNumber?.error
                      ? validation.deliveryPhoneNumber.helperText
                      : undefined
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+974</InputAdornment>,
                  }}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", columnGap: "12px", alignItems: "baseline" }}>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Order
                </Typography>
                <TextField
                  className="form-field"
                  value={driver.order}
                  error={validation.show && validation.order?.error}
                  helperText={validation.show && validation.order?.error ? validation.order.helperText : undefined}
                  onChange={(e) => handleFieldChange("order", e.target.value)}
                />
              </Box>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Status
                </Typography>
                <FormControlLabel
                  sx={{
                    margin: 0,
                    columnGap: "8px",
                    "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 400 },
                  }}
                  control={
                    <Toggle
                      checked={Boolean(driver?.active)}
                      onChange={(e) => handleFieldChange("active", Boolean(e.target.checked))}
                    />
                  }
                  label="Active"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <LoadingButton
            fullWidth
            loading={loading}
            color="secondary"
            variant="contained"
            sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
            onClick={saveChanges}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
